import React, { memo, useCallback, useMemo } from 'react';

import { TopNavigationItemAnchorProps } from '@/components/atomic/molecules/navigation/top-nav-items/item/typings';
import withExposer from '@/hocs/with-exposer';
import useStore from '@/state';
import closeNavigationAction from '@/state/selectors/navigation/close-navigation';
import getDropdownSegment from '@/state/selectors/navigation/get-dropdown-segment';
import getDropdownSegments from '@/state/selectors/navigation/get-dropdown-segments';
import getOpenState from '@/state/selectors/navigation/get-open-state';
import isExpectedDropdownSegment from '@/state/selectors/navigation/is-expected-dropdown-segment';
import pushDropdownSegmentAction from '@/state/selectors/navigation/push-dropdown-segment';
import setGenericDropdownStateAction from '@/state/selectors/navigation/set-generic-dropdown-state';
import { IStore } from '@/state/slices/typings';
import { usePrevious } from '@multitenancy-spa/hooks/esm/hooks/use-previous';

const TopNavItemAnchorComponent: React.FC<TopNavigationItemAnchorProps> = (
	props,
) => {
	const {
		children,
		link,
		ariaLabel,
		fragmentVariationPath,
		label,
		id,
		dropdownSegment,
		$variant,
	} = props;
	const hasDropdown = useMemo(
		() => Boolean(fragmentVariationPath),
		[fragmentVariationPath],
	);

	const expanded = useStore((state: IStore) =>
		isExpectedDropdownSegment(state, 1, fragmentVariationPath ?? ''),
	);
	const open = useStore(getOpenState);

	const dropdownSegments = useStore(getDropdownSegments);
	const previousDropdownSegments = usePrevious(dropdownSegments);

	const secondLevel = useStore((state: IStore) =>
		getDropdownSegment(state, 1),
	);
	const closeNavigation = useStore(closeNavigationAction);
	const setGenericDropdownState = useStore(setGenericDropdownStateAction);
	const pushDropdownSegment = useStore(pushDropdownSegmentAction);

	const onClick = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			if (fragmentVariationPath) {
				e.preventDefault();
				const canClose =
					open &&
					((!dropdownSegments && previousDropdownSegments) ||
						secondLevel === fragmentVariationPath);
				if (canClose) {
					closeNavigation();
				} else {
					setGenericDropdownState('secondLayerTitle', label);
					pushDropdownSegment(fragmentVariationPath, 1, id, $variant);
				}
			}
			if (dropdownSegment) {
				e.preventDefault();
				pushDropdownSegment(dropdownSegment, 2);
			}
			return;
		},
		[
			fragmentVariationPath,
			dropdownSegment,
			open,
			dropdownSegments,
			previousDropdownSegments,
			secondLevel,
			closeNavigation,
			pushDropdownSegment,
			id,
			setGenericDropdownState,
			label,
		],
	);

	const href = link ?? '#';
	const role = link ? 'link' : 'button';

	return (
		<a
			href={href}
			title={ariaLabel}
			aria-label={ariaLabel}
			aria-haspopup={hasDropdown ? 'dialog' : undefined}
			aria-expanded={expanded && open}
			role={role}
			onClick={onClick}
		>
			{children}
		</a>
	);
};

const TopNavItemAnchor = memo(
	withExposer(TopNavItemAnchorComponent, 'topNavItemAnchor'),
);

export default TopNavItemAnchor;
